import moment from 'moment';
import _ from 'lodash';
import {
  SET_PAGE,
  SET_COUNTRY_OPTIONS,
  SET_REGION_OPTIONS,
  SET_CITY_OPTIONS,
  SET_COUNTRY,
  SET_REGION,
  SET_CITY,
  SET_ARRIVAL,
  SET_DURATION,
  SET_OCCUPANCY,
  SET_FILTER_SET,
  RequestActions,
  ActionSetCityOptions,
  ActionSetRegionOptions,
  ActionSetCountryOptions,
  RESET_FILTER_SET,
} from '../actions/types';
import {
  FETCH_PAGE_REQUEST,
  FETCH_PAGE_SUCCESS,
  RoutesActions,
} from '../../../routes/actions/types';
import { SET_MIN_STAY, BookingsActions } from '../../bookings/actions/types';
import initialState, { buildInitialFilterSetState } from '../config/initialState';
import type { RequestState } from '../types';
import * as PageType from '../../../routes/config/pageTypes';
import { loadRequest, autoSyncRequestStore } from '../config/request-store';
import { queryToState } from './queryReducer';
import { isSameType } from '../../../utils/helpers';

const SUITE_DURATION_OF_STAY = 2;

const paginationReset = { page: initialState.page };

function loadInitialState(): RequestState {
  return loadRequest() || initialState;
}

export const stateDiff = (base: RequestState, other: RequestState): any =>
  // Reduces two states to its valid differences
  _.keys(initialState).reduce((diff, param) => {
    switch (param) {
      case 'arrival': {
        const arrival = base.arrival && moment(base.arrival);
        return (arrival === null && arrival !== other.arrival) ||
          (arrival && arrival.isValid() && !arrival.isSame(other.arrival))
          ? { ...diff, arrival }
          : diff;
      }
      case 'filterSet': {
        const filterSet = { ...other.filterSet, ...base.filterSet };
        const isValid = _.keys(initialState.filterSet).every((key) =>
          isSameType((initialState.filterSet as any)[key], (filterSet as any)[key]),
        );
        return isValid && !_.isEqual(filterSet, other.filterSet) ? { ...diff, filterSet } : diff;
      }
      default: {
        const value = (base as any)[param];
        const isValid = !_.isUndefined(value) && isSameType((initialState as any)[param], value);
        return isValid && !_.isEqual(value, (other as any)[param])
          ? { ...diff, [param]: value }
          : diff;
      }
    }
  }, {});

function pageReducer(state: RequestState, action: RoutesActions): RequestState {
  if (action.type === FETCH_PAGE_REQUEST) {
    switch (action.pageType) {
      case PageType.HOMEPAGE:
        return {
          ...state,
          filterSet: initialState.filterSet,
        };
      case PageType.SEARCH: {
        const { location } = action;
        if (_.isEmpty(location.search)) {
          return {
            ...state,
            ...paginationReset,
          };
        }

        const queryState = queryToState(location.search);
        return {
          ...state,
          ...paginationReset,
          ...queryState.requestState,
          filterSet: {
            ...state.filterSet,
            ...queryState.filterSet,
          },
        };
      }
      case PageType.BOOKING: {
        const params = action.location.state && (action.location.state as any).body;
        if (!params) return state;
        return {
          ...state,
          arrival: moment(params.arrival),
          durationOfStay: moment(params.departure).diff(params.arrival, 'days'),
          occupancy: Number.isNaN(params.num_persons)
            ? state.occupancy
            : Number(params.num_persons),
        };
      }
      default:
        return state;
    }
  }

  if (action.type !== FETCH_PAGE_SUCCESS) {
    return state;
  }

  const page = action.data as any;
  switch (action.pageType) {
    case PageType.LANDING_PAGE: {
      const queryState = queryToState(page.searchParams);
      return {
        ...state,
        ...queryState.requestState,
        filterSet: {
          ...state.filterSet,
          ...queryState.filterSet,
        },
      };
    }
    case PageType.HOST: {
      if (!page.searchParams || _.isEmpty(page.searchParams)) {
        return state;
      }
      const queryState = queryToState(page.searchParams);
      return {
        ...state,
        ...queryState.requestState,
        filterSet: {
          ...state.filterSet,
          ...queryState.filterSet,
        },
      };
    }
    default:
      return state;
  }
}

function optionReducer(
  state: RequestState,
  action: ActionSetCityOptions | ActionSetRegionOptions | ActionSetCountryOptions,
): RequestState {
  switch (action.type) {
    case SET_COUNTRY_OPTIONS:
      return {
        ...state,
        options: {
          ...state.options,
          countries: action.countries,
        },
      };
    case SET_REGION_OPTIONS:
      return {
        ...state,
        options: {
          ...state.options,
          regions: action.regions,
        },
      };
    case SET_CITY_OPTIONS:
      return {
        ...state,
        options: {
          ...state.options,
          cities: action.cities,
        },
      };
    default:
      return state;
  }
}

function isSuiteActivated(state: RequestState, action: RequestActions): boolean {
  if (action.type === SET_FILTER_SET) {
    return (
      _.isEqual(action.filterSet.accommodation_type, ['suite']) &&
      !_.isEqual(state.filterSet.accommodation_type, ['suite'])
    );
  }
  return false;
}

function requestReducer(
  state: RequestState = loadInitialState(),
  action: RequestActions | RoutesActions | BookingsActions,
): RequestState {
  switch (action.type) {
    case SET_COUNTRY_OPTIONS:
    case SET_REGION_OPTIONS:
    case SET_CITY_OPTIONS:
      return optionReducer(state, action);
    case SET_PAGE:
      return {
        ...state,
        page: action.page,
      };
    case SET_COUNTRY:
      return {
        ...state,
        ...paginationReset,
        countryCode: action.countryCode,
        region: initialState.region,
        city: initialState.city,
        options: {
          ...state.options,
          regions: initialState.options.regions,
          cities: initialState.options.cities,
        },
      };
    case SET_REGION:
      return {
        ...state,
        ...paginationReset,
        countryCode: action.countryCode,
        region: action.region,
        city: initialState.city,
        options: {
          ...state.options,
          cities: initialState.options.cities,
        },
      };
    case SET_CITY:
      return {
        ...state,
        ...paginationReset,
        countryCode: action.countryCode,
        region: action.region,
        city: action.city,
      };
    case SET_ARRIVAL:
      return {
        ...state,
        ...paginationReset,
        arrival: action.arrival,
      };
    case SET_DURATION:
      return {
        ...state,
        ...paginationReset,
        durationOfStay: action.durationOfStay,
      };
    case SET_OCCUPANCY:
      return {
        ...state,
        ...paginationReset,
        occupancy: action.occupancy,
      };
    case SET_FILTER_SET: {
      if (isSuiteActivated(state, action)) {
        return {
          ...state,
          ...paginationReset,
          durationOfStay: SUITE_DURATION_OF_STAY,
          filterSet: {
            ...state.filterSet,
            ...action.filterSet,
          },
        };
      }
      return {
        ...state,
        ...paginationReset,
        filterSet: {
          ...state.filterSet,
          ...action.filterSet,
        },
      };
    }
    case FETCH_PAGE_REQUEST:
    case FETCH_PAGE_SUCCESS:
      return pageReducer(state, action);
    case SET_MIN_STAY: {
      if (state.durationOfStay == null || action.minStay > state.durationOfStay) {
        return { ...state, durationOfStay: action.minStay };
      }
      return state;
    }
    case RESET_FILTER_SET:
      return {
        ...state,
        filterSet: buildInitialFilterSetState(),
      };
    default:
      return state;
  }
}

autoSyncRequestStore();

export default requestReducer;
export * from './queryReducer';
